package com.klantenstop.web.pages.authentication.resetPassword

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.klantenstop.web.navigation.Navigation
import com.klantenstop.web.navigation.unauthenticatedGuard
import com.klantenstop.web.pages.authentication.components.AuthenticationContainer
import com.klantenstop.web.pages.authentication.registration.authLinkModifier
import com.materialkobweb.styles.MaterialColorVars
import com.klantenstop.web.utils.Constants.PASSWORD_MIN_LENGTH
import com.klantenstop.web.utils.Res
import com.klantenstop.web.utils.format
import com.materialkobweb.utils.rememberViewModel
import com.klantenstop.web.utils.withAlpha
import com.materialkobweb.components.DsPageLayout
import com.materialkobweb.components.widgets.DsEditableField
import com.materialkobweb.components.widgets.FilledButton
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Text

@Page("/wachtwoord-resetten")
@Composable
fun ResetPasswordPage() = unauthenticatedGuard {
    val pageContext = rememberPageContext()
    val token = remember { pageContext.route.params["token"] ?: "" }

    val viewModel = rememberViewModel(cached = false) { ResetPasswordViewModel(token) }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.effect.collect { event ->
            when (event) {
                ResetPasswordContract.Effect.NavigateToLogin -> {
                    pageContext.router.navigateTo(
                        pathQueryAndFragment = Navigation.Screen.Login().route,
                        updateHistoryMode = UpdateHistoryMode.REPLACE
                    )
                }
            }
        }
    }

    ResetPasswordPageContent(
        state = state,
        sendIntent = viewModel::sendIntent
    )
}

@Composable
private fun ResetPasswordPageContent(
    state: ResetPasswordContract.State,
    sendIntent: (ResetPasswordContract.Intent) -> Unit
) {

    DsPageLayout(
        modifier = Modifier.fillMaxSize(),
        title = Res.String.DocumentTitle.format("Wachtwoord resetten"),
        header = {},
        footer = {}
    ) {
        AuthenticationContainer(
            title = "Nieuw wachtwoord instellen",
            content = {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(16.px, Alignment.CenterVertically)
                ) {
                    SpanText(
                        text = "Voer je nieuwe wachtwoord in. Het moet minimaal 8 karakters bevatten.",
                        modifier = Modifier
                            .fillMaxWidth()
                            .fontSize(14.px)
                            .lineHeight(20.px)
                            .color(MaterialColorVars.OnSurfaceVariant.value())
                            .margin(bottom = 8.px)
                    )

                    Column(
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        DsEditableField(
                            modifier = Modifier.fillMaxWidth(),
                            id = "reset-password-password",
                            label = "Nieuw wachtwoord",
                            placeholder = "Nieuw wachtwoord",
                            value = state.password,
                            valid = !state.hasAttemptedReset || state.password.length >= PASSWORD_MIN_LENGTH,
                            onValueChange = { sendIntent(ResetPasswordContract.Intent.UpdatePassword(it)) },
                            type = InputType.Password,
                            required = true,
                            backgroundColor = MaterialColorVars.SurfaceContainer.value(),
                            focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
                        )

                        SpanText(
                            text = "Minimum 8 tekens waarvan minstens 1 hoofdletter, 1 cijfer en 1 symbool",
                            modifier = Modifier
                                .margin(left = 8.px, top = 4.px)
                                .fontSize(12.px)
                                .lineHeight(14.px)
                                .color(MaterialColorVars.Outline.value())
                        )
                    }

                    DsEditableField(
                        modifier = Modifier.fillMaxWidth(),
                        id = "reset-password-confirmation",
                        label = "Herhaal wachtwoord",
                        placeholder = "Herhaal wachtwoord",
                        value = state.passwordConfirmation,
                        valid = !state.hasAttemptedReset || (state.passwordConfirmation.isNotEmpty() && state.password == state.passwordConfirmation),
                        onValueChange = { sendIntent(ResetPasswordContract.Intent.UpdatePasswordConfirmation(it)) },
                        type = InputType.Password,
                        required = true,
                        backgroundColor = MaterialColorVars.SurfaceContainer.value(),
                        focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f),
                        onCommit = { sendIntent(ResetPasswordContract.Intent.SubmitResetPassword) }
                    )
                }

                FilledButton(
                    modifier = Modifier.fillMaxWidth(),
                    type = ButtonType.Submit,
                    onClick = { sendIntent(ResetPasswordContract.Intent.SubmitResetPassword) },
                    enabled = !state.isLoading
                ) {
                    Text(if (state.isLoading) "Bezig..." else "Wachtwoord resetten")
                }
            },
            footer = {
                Text("Weet je je wachtwoord weer? ")
                Link(
                    modifier = authLinkModifier,
                    path = Navigation.Screen.Login().route,
                    updateHistoryMode = UpdateHistoryMode.REPLACE,
                    text = "Log in"
                )
            }
        )
    }
}

